/**
 * AUTHENTICATION LAYER
 * Handles user sessions, login, signup, and role management.
 */

import type { Session, User as SupabaseUser, SupabaseClient } from '@supabase/supabase-js';
import { User, UserData, UserRole, UserStatus } from '../domain/entities/User';
import { UserId, asUserId } from '../domain/types/BrandedTypes';
import { registry } from './registry';
import { findFakeToken } from './mock-detector';
import { getErrorMessage } from '../utils/errorUtils';
import type { DatabaseModuleType } from './supabase';
import { authService } from './auth.service';
import { userRealtimeChannel } from './user-realtime-channel';
import SmartStore from './store';
import { track } from '@shared/analytics/track';

// Raw Database Structure (Internal to this module)
interface RawUserProfile {
    id: UserId;
    username: string;
    full_name: string;
    email: string; // Required by store.ts
    name: string;  // Required by store.ts (alias for full_name)
    avatar_url: string;
    role: 'super_admin' | 'admin' | 'teacher' | 'student';
    status: 'pending' | 'active' | 'suspended' | 'banned';
    tenant_id?: string;
    phone?: string;
    total_xp?: number;
    streak_count?: number;
    lives?: number;
    gems?: number;
    tenants?: {
        name: string;
        code: string;
        config?: Record<string, unknown>;
    };
    [key: string]: unknown;
}

interface SignUpData {
    user: SupabaseUser | null;
    session: Session | null;
}


interface SmartAuthType {
    get user(): User | null;
    get supabaseUser(): SupabaseUser | null;
    signUp(email: string, password: string, username: string, fullName: string, phone?: string): Promise<SignUpData>;
    signIn(email: string, password: string): Promise<{ user: User; session: Session }>;
    signInWithGoogle(redirectTo?: string): Promise<void>;
    signOut(): Promise<void>;
    getCurrentSession(): Promise<Session | null>;
    getUserProfile(): Promise<User | null>;
    refreshProfileInBackground(): Promise<User | null>;
    requireAuth(): Promise<Session | null>;
    checkAccess(profile: User | null): void;
    showStatusOverlay(status: string): void;
    applyPreviewMode(): void;
}

// Internal State (Private to this module - No Window Leaks 🛡️)
let _cachedSession: Session | undefined;
let _profileFetching: Promise<User | null> | null = null;
let _lastProfileRefresh = 0;

// Extend Window interface
declare global {
    interface Window {
        SmartAuth: SmartAuthType;
        SmartMonitoring?: {
            identifyUser(profile: RawUserProfile): void;
        };
        ReactiveStore?: import('./store').IReactiveStore;
        __SMART_MOCK_ACTIVE?: boolean;
    }
}


const SmartAuth: SmartAuthType = {
    // Getter for sync user access
    get user(): User | null {
        // 1. Priority: Use Store if available (has full profile including tenant_id)
        const store = registry.get<import('./store').ReactiveStore>('Store');
        const storeUser = store?.get('user');
        if (storeUser) {
            return storeUser;
        }

        // 2. Fallback: Use sessionStorage cache (has full profile from DB)
        const cachedProfile = sessionStorage.getItem('idiomaclub_profile_cache');
        if (cachedProfile) {
            try {
                const parsed = JSON.parse(cachedProfile);
                return new User({ ...parsed, id: asUserId(parsed.id) } as UserData);
            } catch { /* ignore parse error */ }
        }

        // 3. Last resort: Use session metadata (may lack tenant_id)
        if (!_cachedSession?.user) return null;
        const metadata = _cachedSession.user.user_metadata || {};
        return new User({
            id: asUserId(_cachedSession.user.id),
            email: _cachedSession.user.email || '',
            username: String(metadata.username || ''),
            full_name: String(metadata.full_name || ''),
            role: (metadata.role as UserRole) || 'student',
            status: (metadata.status as UserStatus) || 'active',
            avatar_url: String(metadata.avatar_url || ''),
            tenant_id: (metadata.tenant_id as unknown as import('../domain/types/BrandedTypes').TenantId) || undefined
        });
    },

    get supabaseUser(): SupabaseUser | null {
        return _cachedSession?.user || null;
    },

    // --- AUTH ACTIONS ---

    async signUp(email: string, password: string, username: string, fullName: string, phone: string = ''): Promise<SignUpData> {
        const supabase = registry.get('Supabase');
        if (!supabase) throw new Error("Supabase not initialized");

        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    username: username,
                    full_name: fullName,
                    phone: phone,
                    avatar_url: `https://api.dicebear.com/7.x/avataaars/svg?seed=${username}`,
                    role: 'student',
                    tenant_id: this.user?.tenantId || 'idiomaclub' // Fallback to main tenant
                }
            }
        });

        if (error) throw error;
        return data;
    },

    async signIn(email: string, password: string): Promise<{ user: User; session: Session }> {
        const supabase = registry.get('Supabase');
        if (!supabase) throw new Error("Supabase not initialized");

        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        });

        if (error) throw error;

        if (error) throw error;

        // Map to domain User
        const metadata = data.user.user_metadata || {};
        const userEntity = new User({
            id: asUserId(data.user.id),
            email: data.user.email || '',
            username: String(metadata.username || ''),
            full_name: String(metadata.full_name || ''),
            role: (metadata.role as UserRole) || 'student',
            status: (metadata.status as UserStatus) || 'active',
            avatar_url: String(metadata.avatar_url || ''),
            tenant_id: metadata.tenant_id
        });

        track('login_success', { method: 'password' });

        return { user: userEntity, session: data.session };
    },

    /**
     * 🚀 GOOGLE SOCIAL LOGIN
     * Initiates OAuth flow with Google provider.
     * After successful auth, Supabase redirects to the specified URL.
     * Profile is auto-created via database trigger (handle_new_user).
     */
    async signInWithGoogle(redirectTo?: string): Promise<void> {
        const supabase = registry.get('Supabase');
        if (!supabase) throw new Error("Supabase not initialized");

        // Get current tenant from session or use default The Idioma Club tenant
        const currentTenant = this.user?.tenantId || '3429bbbe-2b74-47eb-bab2-12929af46e58';

        console.log('🔐 Initiating Google OAuth with tenant:', currentTenant);

        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: redirectTo || `${window.location.origin}/#login`,
                queryParams: {
                    access_type: 'offline',
                    prompt: 'consent',
                },
                // 🛡️ Pass metadata for profile creation trigger
                // Note: Supabase's handle_new_user trigger will use this
                skipBrowserRedirect: false
            }
        });

        if (error) {
            console.error('❌ Google OAuth error:', error.message);
            throw error;
        }

        // OAuth redirects the browser; this code won't execute after redirect
        console.log('🔄 Redirecting to Google...');
    },

    async signOut(): Promise<void> {
        const supabase = registry.get('Supabase');

        // 0. CLOSE CONSOLIDATED REALTIME CHANNELS — prevent cross-user leak
        void userRealtimeChannel.disconnect();

        // 1. CLEAR REACT STORE — prevents ghost user state if redirect fails (ATTACK_PLAN B1)
        // Triggers AccessGate's user-change effect which also clears _accessCache (B2).
        SmartStore.actions.setUser(null);

        // 2. CLEAR LOCAL DATA (Critical)
        const authKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-') || k.includes('auth'));
        authKeys.forEach(k => localStorage.removeItem(k));
        sessionStorage.removeItem('idiomaclub_profile_cache');

        // Clear cached session
        _cachedSession = undefined;
        _profileFetching = null;

        // 3. TRIGGER REDIRECT IMMEDIATELY (Don't wait for server if network is flaky)
        const redirect = () => { window.location.href = '/#/login'; };

        if (supabase) {
            // Race: redirect as soon as server responds OR after 1s timeout — whichever comes first
            Promise.race([
                supabase.auth.signOut(),
                new Promise<void>(resolve => setTimeout(resolve, 1000))
            ]).finally(() => redirect());
        } else {
            redirect();
        }
    },

    // --- SESSION MANAGEMENT ---

    async getCurrentSession(): Promise<Session | null> {
        // 🛡️ Ensure Database is initialized (Phase 6.3 Cleanup)
        let supabase: SupabaseClient | null = registry.get<SupabaseClient>('Supabase') ?? null;
        if (!supabase) {
            const db = registry.get<DatabaseModuleType>('Database');
            if (db) {
                supabase = await db.init();
            }
        }

        if (!supabase) return null;

        // 🛡️ DEV BYPASS — only active in local dev builds, stripped from production
        if (import.meta.env.DEV) {
            const authToken = findFakeToken();
            if (authToken) {
                window.__SMART_MOCK_ACTIVE = true;
                console.log('✅ DEV BYPASS - Returning mock session');
                const mockSession = JSON.parse(authToken);
                _cachedSession = mockSession as Session;
                return mockSession as Session;
            }
        }

        // 🛡️ Session Verification: Always check Supabase to prevent stale userId in multi-step flows
        const { data: { session } } = await supabase.auth.getSession();

        if (session) _cachedSession = session;
        else _cachedSession = undefined;

        return session;
    },

    async getUserProfile(): Promise<User | null> {
        // 🚀 PERF: Return in-flight promise to deduplicate concurrent calls
        if (_profileFetching) return _profileFetching;

        // 🛡️ DEV BYPASS: Only in local dev builds — stripped from production
        if (import.meta.env.DEV) {
            const authToken = findFakeToken();
            if (authToken) {
                window.__SMART_MOCK_ACTIVE = true;
                console.log('✅ DEV BYPASS DETECTED - Returning mock admin profile');
                const mockAdminProfile = {
                    id: asUserId('dev-admin'),
                    username: 'DevAdmin',
                    full_name: 'Developer Admin',
                    name: 'Developer Admin',
                    email: 'admin@smartt.com',
                    role: 'admin',
                    avatar_url: 'https://api.dicebear.com/7.x/avataaars/svg?seed=admin',
                    streak: 365,
                    gems: 1000,
                    health_score: 100,
                    status: 'active',
                    cohort_id: null,
                    cohort_name: 'Dev Team',
                    tenant_id: 'dev',
                    tenants: { name: 'Dev Mode', code: 'DEV', config: {} }
                } as RawUserProfile;

                const userEntity = new User({
                    ...mockAdminProfile,
                    id: asUserId(mockAdminProfile.id),
                    tenant_id: mockAdminProfile.tenant_id
                } as UserData);

                sessionStorage.setItem('idiomaclub_profile_cache', JSON.stringify(mockAdminProfile));
                const store = registry.get<import('./store').IReactiveStore>('Store');
                if (store) store.actions.setUser(userEntity);
                return userEntity;
            }
        }

        const cacheKey = 'idiomaclub_profile_cache';
        const cachedProfile = sessionStorage.getItem(cacheKey);

        if (cachedProfile) {
            try {
                const parsed = JSON.parse(cachedProfile);
                const userEntity = new User({ ...parsed, id: asUserId(parsed.id) } as UserData);
                const store = registry.get<import('./store').IReactiveStore>('Store');
                if (store) store.actions.setUser(userEntity);

                if (!_lastProfileRefresh ||
                    Date.now() - _lastProfileRefresh > 60000) {
                    _lastProfileRefresh = Date.now();
                    this.refreshProfileInBackground();
                }
                return userEntity;
            } catch {
                // Cache parse failed — fall through to refreshProfileInBackground
            }
        }

        return await this.refreshProfileInBackground();
    },

    async refreshProfileInBackground(): Promise<User | null> {
        if (_profileFetching) return _profileFetching;

        _profileFetching = (async (): Promise<User | null> => {
            try {
                const supabase = registry.get('Supabase');
                if (!supabase) return null;

                // 🚀 PERF: Use authService instead of supabase.auth directly
                // This prevents acquiring a new auth lock since the session is already cached
                const user = await authService.getUser();

                if (!user) return null;

                const { data: profile, error } = await supabase
                    .from('profiles')
                    .select('*, tenants(name, code, config)')
                    .eq('id', user.id)
                    .maybeSingle();

                if (!error && profile) {
                    sessionStorage.setItem('idiomaclub_profile_cache', JSON.stringify(profile));

                    if (window.SmartMonitoring) {
                        window.SmartMonitoring.identifyUser(profile as RawUserProfile);
                    }

                    const userEntity = new User({ ...profile, id: asUserId(profile.id) } as UserData);
                    const store = registry.get<import('./store').IReactiveStore>('Store');
                    if (store) {
                        store.actions.setUser(userEntity);
                    }

                    window.dispatchEvent(new CustomEvent('profileUpdated', { detail: profile }));
                    return userEntity;
                }

                return null;
            } catch (err) {
                console.error('refreshProfileInBackground error:', getErrorMessage(err));
                return null;
            } finally {
                _profileFetching = null;
            }
        })();

        return _profileFetching;
    },

    // --- PROTECTED ROUTE GUARD ---

    async requireAuth(): Promise<Session | null> {
        const session = await this.getCurrentSession();
        if (!session) {
            window.location.href = '/#login';
            return null;
        }

        const profile = await this.getUserProfile();
        this.checkAccess(profile);
        return session;
    },

    checkAccess(profile: User | null): void {
        if (!profile) return;

        const canBypass = ['admin', 'teacher'].includes(profile.role);
        if (profile.status !== 'active' && !canBypass) {
            this.showStatusOverlay(profile.status);
        }
    },

    showStatusOverlay(status: string): void {
        if (status === 'active') return;

        if (status === 'pending') {
            this.applyPreviewMode();
            return;
        }

        if (document.getElementById('status-blocking-overlay')) return;

        const overlay = document.createElement('div');
        overlay.id = 'status-blocking-overlay';
        overlay.style.cssText = `
            position: fixed; top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(15, 23, 42, 0.9); backdrop-filter: blur(10px);
            z-index: 99999; display: flex; align-items: center; justify-content: center; padding: 20px;
        `;

        const title = status === 'suspended' ? "Cuenta Suspendida ⏸️" : "Acceso Denegado 🚫";
        const msg = status === 'suspended'
            ? "Tu acceso ha sido pausado temporalmente. Contacta a soporte."
            : "Esta cuenta ha sido inhabilitada permanentemente.";

        overlay.innerHTML = `
            <div style="background: white; padding: 40px; border-radius: 32px; max-width: 400px; width: 100%; text-align: center;">
                <h2 style="font-family: 'Outfit', sans-serif; font-size: 24px; font-weight: 800; margin-bottom: 16px;">${title}</h2>
                <p style="color: var(--sc-slate-500); margin-bottom: 32px;">${msg}</p>
                <button onclick="SmartAuth.signOut()" style="width: 100%; padding: 16px; background: var(--color-indigo); color: white; border: none; border-radius: 16px; font-weight: 700; cursor: pointer;">
                    Cerrar sesión
                </button>
            </div>
        `;
        document.body.appendChild(overlay);
        document.body.style.overflow = 'hidden';
    },

    applyPreviewMode(): void {
        const container = document.querySelector<HTMLElement>('.page-container');
        const header = document.querySelector<HTMLElement>('.top-header');

        if (container) {
            container.style.filter = 'grayscale(100%) opacity(0.7)';
            container.style.pointerEvents = 'none';
            container.style.userSelect = 'none';
        }

        if (header) {
            header.style.filter = 'grayscale(100%)';
            header.style.pointerEvents = 'none';
        }

        const nav = document.querySelector<HTMLElement>('.bottom-nav-container');
        if (nav) {
            nav.style.filter = 'none';
            nav.style.pointerEvents = 'auto';
        }

        setTimeout(() => {
            const logoutItems = document.querySelectorAll<HTMLElement>('.menu-item.danger, .logout-btn, [onclick*="signOut"]');
            logoutItems.forEach(item => {
                item.style.filter = 'none';
                item.style.pointerEvents = 'auto';
                item.style.opacity = '1';
                const menu = item.closest<HTMLElement>('.settings-menu');
                if (menu) menu.style.pointerEvents = 'auto';
            });
        }, 100);
    }
};

// Phase 6.3: Register via direct import
// Phase 6.3: Idempotent Registration
if (!registry.has('Auth')) {
    registry.register('Auth', SmartAuth);
}


export default SmartAuth;
export type { RawUserProfile, SmartAuthType };
